import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ShieldCheck, Leaf, HeartStraight } from "@phosphor-icons/react";

const steps = [
  { key: "welcome", title: "Welcome to Gutvibe Health" },
  { key: "consent", title: "Your permissions" },
  { key: "profile", title: "Tell us about you" },
  { key: "emergency", title: "Emergency preferences" },
];

const consentItems = [
  { key: "terms_accepted", label: "I accept the Terms of Service", required: true },
  { key: "medical_disclaimer", label: "I understand Priya (Gutvibe AI) is NOT a doctor and does not diagnose or prescribe", required: true },
  { key: "microphone", label: "Allow microphone for voice conversations", required: false },
  { key: "camera", label: "Allow camera for optional wellness scans", required: false },
  { key: "location", label: "Allow precise location for nearby hospitals/labs", required: false },
  { key: "lab_sharing", label: "Allow sharing my reports with our medical team for review", required: false },
  { key: "clinician_sharing", label: "Allow sharing my profile with doctors I book", required: false },
  { key: "research", label: "Allow anonymised research use (optional)", required: false },
  { key: "marketing", label: "Send me health tips and offers", required: false },
  { key: "testimonials", label: "I may agree to share testimonials later", required: false },
];

export default function Onboarding() {
  const { refresh, user } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [consent, setConsent] = useState({});
  const [profile, setProfile] = useState({
    gender: "", dob: "", height_cm: "", weight_kg: "", blood_group: "",
    region: "Kerala", language: "en", allergies: "", conditions: "", goals: "",
  });
  const [emergency, setEmergency] = useState([{ name: "", phone: "", relation: "" }]);
  const [saving, setSaving] = useState(false);

  const next = async () => {
    if (step === 1) {
      const missing = consentItems.filter((c) => c.required && !consent[c.key]);
      if (missing.length) { toast.error("Please accept the required items to continue"); return; }
      setSaving(true);
      try {
        await api.put("/consent", consent);
      } catch (e) {
        toast.error("Could not save consent — please try again");
        setSaving(false);
        return;
      } finally { setSaving(false); }
    }
    if (step === 3) {
      setSaving(true);
      try {
        await api.put("/profile", {
          ...profile,
          height_cm: profile.height_cm ? Number(profile.height_cm) : null,
          weight_kg: profile.weight_kg ? Number(profile.weight_kg) : null,
          allergies: profile.allergies ? profile.allergies.split(",").map(s => s.trim()).filter(Boolean) : [],
          conditions: profile.conditions ? profile.conditions.split(",").map(s => s.trim()).filter(Boolean) : [],
          goals: profile.goals ? profile.goals.split(",").map(s => s.trim()).filter(Boolean) : [],
          emergency_contacts: emergency.filter(c => c.phone),
        });
        await refresh();
        toast.success("Profile set up. Welcome aboard.");
        nav("/app");
      } catch (e) {
        toast.error("Could not save profile");
      } finally { setSaving(false); }
      return;
    }
    setStep(step + 1);
  };

  return (
    <div className="min-h-screen bg-bone flex items-center justify-center p-4 grain">
      <motion.div
        key={step}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="w-full max-w-2xl bg-white rounded-3xl border border-black/5 p-8 md:p-12"
      >
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="overline text-terracotta">Step {step + 1} / {steps.length}</div>
            <h1 className="font-heritage text-4xl text-ayurveda mt-1">{steps[step].title}</h1>
          </div>
          <div className="flex gap-1.5">
            {steps.map((_, i) => (
              <div key={i} className={`h-1.5 w-8 rounded-full ${i <= step ? "bg-terracotta" : "bg-black/10"}`} />
            ))}
          </div>
        </div>

        {step === 0 && (
          <div className="space-y-6">
            <p className="font-heritage text-2xl text-charcoal/90 leading-relaxed">
              Hello {user?.name?.split(" ")[0]}. I'm Priya, your 30-year-old Malayali AI health companion at Gutvibe Health.
            </p>
            <p className="text-charcoal/70 leading-relaxed">
              I'll help you navigate care — from finding a trusted doctor to understanding your lab reports, from regional nutrition to emergency support. I am <strong>not a doctor</strong>. I speak your language. I don't diagnose. When something needs a human, I connect you to one.
            </p>
            <div className="grid grid-cols-3 gap-3 pt-4">
              {[
                { icon: ShieldCheck, label: "Consent-first" },
                { icon: Leaf, label: "Nutrition rooted in Kerala" },
                { icon: HeartStraight, label: "Human-supervised" },
              ].map((i) => (
                <div key={i.label} className="rounded-2xl border border-black/5 p-4 text-center bg-bone">
                  <i.icon size={22} weight="duotone" className="text-ayurveda mx-auto mb-2" />
                  <div className="text-xs text-charcoal/70">{i.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-2">
            {consentItems.map((c) => (
              <label key={c.key} className="flex items-start gap-3 p-3 rounded-xl border border-black/5 hover:bg-bone cursor-pointer">
                <Checkbox
                  id={c.key}
                  checked={!!consent[c.key]}
                  onCheckedChange={(v) => setConsent({ ...consent, [c.key]: !!v })}
                  data-testid={`consent-${c.key}`}
                />
                <div className="text-sm text-charcoal/90 leading-relaxed">
                  {c.label} {c.required && <span className="text-terracotta">*</span>}
                </div>
              </label>
            ))}
          </div>
        )}

        {step === 2 && (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Gender</Label>
              <Select value={profile.gender} onValueChange={(v) => setProfile({ ...profile, gender: v })}>
                <SelectTrigger data-testid="onb-gender"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                  <SelectItem value="prefer_not">Prefer not to say</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Region</Label>
              <Select value={profile.region} onValueChange={(v) => setProfile({ ...profile, region: v })}>
                <SelectTrigger data-testid="onb-region"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Kerala">Kerala</SelectItem>
                  <SelectItem value="Malabar">Malabar</SelectItem>
                  <SelectItem value="Thiruvananthapuram">Thiruvananthapuram</SelectItem>
                  <SelectItem value="Pathanamthitta">Pathanamthitta</SelectItem>
                  <SelectItem value="South India">South India</SelectItem>
                  <SelectItem value="Pan-India">Pan-India</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Height (cm)</Label>
              <Input type="number" value={profile.height_cm} onChange={(e) => setProfile({ ...profile, height_cm: e.target.value })} data-testid="onb-height" />
            </div>
            <div className="space-y-2">
              <Label>Weight (kg)</Label>
              <Input type="number" value={profile.weight_kg} onChange={(e) => setProfile({ ...profile, weight_kg: e.target.value })} data-testid="onb-weight" />
            </div>
            <div className="space-y-2 col-span-2">
              <Label>Language</Label>
              <Select value={profile.language} onValueChange={(v) => setProfile({ ...profile, language: v })}>
                <SelectTrigger data-testid="onb-language"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="ml">Malayalam</SelectItem>
                  <SelectItem value="hi">Hindi</SelectItem>
                  <SelectItem value="ta">Tamil</SelectItem>
                  <SelectItem value="kn">Kannada</SelectItem>
                  <SelectItem value="te">Telugu</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 col-span-2">
              <Label>Known allergies (comma separated)</Label>
              <Input value={profile.allergies} onChange={(e) => setProfile({ ...profile, allergies: e.target.value })} placeholder="e.g. Peanuts, Penicillin" data-testid="onb-allergies" />
            </div>
            <div className="space-y-2 col-span-2">
              <Label>Health goals</Label>
              <Textarea value={profile.goals} onChange={(e) => setProfile({ ...profile, goals: e.target.value })} placeholder="e.g. Better sleep, manage weight, reduce stress" data-testid="onb-goals" />
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <p className="text-sm text-charcoal/70">Add at least one emergency contact. We only reach them if you trigger SOS.</p>
            {emergency.map((c, idx) => (
              <div key={idx} className="grid grid-cols-3 gap-3">
                <Input placeholder="Name" value={c.name} onChange={(e) => { const n = [...emergency]; n[idx].name = e.target.value; setEmergency(n); }} data-testid={`onb-emergency-name-${idx}`} />
                <Input placeholder="Phone" value={c.phone} onChange={(e) => { const n = [...emergency]; n[idx].phone = e.target.value; setEmergency(n); }} data-testid={`onb-emergency-phone-${idx}`} />
                <Input placeholder="Relation" value={c.relation} onChange={(e) => { const n = [...emergency]; n[idx].relation = e.target.value; setEmergency(n); }} data-testid={`onb-emergency-relation-${idx}`} />
              </div>
            ))}
            <Button variant="outline" onClick={() => setEmergency([...emergency, { name: "", phone: "", relation: "" }])} data-testid="onb-add-contact">
              + Add another contact
            </Button>
          </div>
        )}

        <div className="mt-10 flex justify-between">
          <Button variant="ghost" onClick={() => step > 0 && setStep(step - 1)} disabled={step === 0} data-testid="onb-back">Back</Button>
          <Button onClick={next} disabled={saving} className="bg-ayurveda hover:bg-ayurveda/90 text-bone h-11 px-8" data-testid="onb-next">
            {saving ? "Saving…" : step === steps.length - 1 ? "Finish" : "Continue"}
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
